import { Check, TriangleAlert } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { TaskTimelineStep } from "@/hooks/useWidgetTabs"
import { cn } from "@/lib/utils"

/**
 * The full stage timeline of one task, shown in the widget panel — what's
 * behind (done), what's happening now, and what's ahead, grouped under
 * Za nami / Teraz / Przed nami. A compact list: marker + label + a time or
 * detail, no connector lines. Every task widget in the Agent thread opens
 * this same view for its task.
 */
const SECTION_LABEL_KEYS = {
  done: "chat.taskTimeline.done",
  current: "chat.taskTimeline.current",
  upcoming: "chat.taskTimeline.upcoming",
} as const satisfies Record<TaskTimelineStep["status"], string>

const Marker = ({ step }: { step: TaskTimelineStep }) => {
  const warn = step.tone === "warn"
  if (step.status === "done") {
    return (
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
          warn
            ? "bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400"
            : "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400",
        )}
      >
        {warn ? (
          <TriangleAlert className="h-3 w-3" />
        ) : (
          <Check className="h-3 w-3" strokeWidth={3} />
        )}
      </span>
    )
  }
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
      <span
        className={cn(
          "h-3 w-3 rounded-full",
          step.status === "current"
            ? "bg-base-primary ring-4 ring-base-primary/15"
            : "border-2 border-muted-foreground/30",
        )}
      />
    </span>
  )
}

export const TaskTimeline = ({ steps }: { steps: TaskTimelineStep[] }) => {
  const { t } = useTranslation()
  return (
    <ol className="flex flex-col">
      {steps.map((step, i) => {
        const newSection = i === 0 || steps[i - 1].status !== step.status
        return (
          <li key={i}>
            {newSection && (
              <div
                className={cn(
                  "mb-1 text-xs font-medium text-muted-foreground",
                  i > 0 && "mt-3",
                )}
              >
                {t(SECTION_LABEL_KEYS[step.status])}
              </div>
            )}
            <div className="flex items-center gap-2.5 py-1.5">
              <Marker step={step} />
              <div className="flex min-w-0 flex-1 items-baseline justify-between gap-2">
                <span
                  className={cn(
                    "text-sm",
                    step.status === "upcoming"
                      ? "text-muted-foreground"
                      : "font-medium text-foreground",
                    step.status === "current" &&
                      "font-semibold !text-base-primary",
                  )}
                >
                  {step.label}
                </span>
                {step.detail && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {step.detail}
                  </span>
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

export default TaskTimeline
